/**
 * API服务层
 */

import axios, { AxiosResponse } from 'axios';
import {
  ApiResponse,
  PaginationParams,
  PaginatedResponse,
  City,
  TrainingBusiness,
  TrainingElement,
  TrainingStandard,
  InspectionReport,
  InspectionSummary,
  BatchInfo,
  WarningStats,
  StatsOverview,
  CityFormData,
  TrainingBusinessFormData,
  TrainingElementFormData,
  TrainingStandardFormData,
  User,
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  UserRole,
  TrainingInfoCollection,
  CreateTrainingInfoInput,
  UpdateTrainingInfoInput,
  TrainingInfoQueryParams,
  TrainingInfoStats,
  ExportTrainingInfoRequest,
  ExportTrainingInfoResponse,
  BatchTrainingInfoRequest,
  BatchTrainingInfoResponse,
} from '../types';

// API基础URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从localStorage获取token并添加到请求头
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('🚀 API Request:', config.method?.toUpperCase(), config.url, {
      baseURL: config.baseURL,
      headers: config.headers,
      data: config.data instanceof FormData ? 'FormData' : config.data
    });
    return config;
  },
  (error) => {
    console.error('🚨 Request Error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 如果是 blob 响应（文件下载），直接返回，不做 JSON 检查
    if (response.config.responseType === 'blob') {
      console.log('📥 Blob Response:', response.status, response.config.url);
      return response;
    }

    console.log('✅ API Response:', response.status, response.config.url, {
      success: response.data.success,
      message: response.data.message,
      dataType: typeof response.data.data,
      hasError: !!response.data.error
    });

    // 特别调试warnings接口
    if (response.config.url?.includes('/warnings')) {
      console.log('🔍 [INTERCEPTOR] warnings响应详情:', {
        url: response.config.url,
        responseData: response.data,
        dataKeys: Object.keys(response.data.data || {}),
        rawData: JSON.stringify(response.data).substring(0, 200) + '...'
      });
    }

    if (!response.data.success) {
      console.error('❌ API Logic Error:', {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data
      });
      throw new Error(response.data.error || response.data.message || 'API请求失败');
    }

    return response;
  },
  (error) => {
    console.error('❌ API Network/HTTP Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL
      }
    });

    // 处理401未授权错误 - 自动跳转到登录页面
    if (error.response?.status === 401) {
      console.log('🚪 检测到401错误，清除本地认证信息并跳转到登录页面');
      // 清除本地存储的认证信息
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // 跳转到登录页面
      window.location.href = '/login';
      return Promise.reject(new Error('认证失败，请重新登录'));
    }

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('网络请求失败，请检查网络连接');
    }
  }
);

// ===== 健康检查 =====
export const healthCheck = async (): Promise<any> => {
  const response = await api.get<ApiResponse>('/health');
  return response.data.data;
};

// ===== 城市管理 API =====
export const getCities = async (params?: PaginationParams): Promise<PaginatedResponse<City>> => {
  const response = await api.get<ApiResponse<PaginatedResponse<City>>>('/api/cities', { params });
  return response.data.data!;
};

export const getCity = async (id: number): Promise<City> => {
  const response = await api.get<ApiResponse<City>>(`/api/cities/${id}`);
  return response.data.data!;
};

export const createCity = async (data: CityFormData): Promise<City> => {
  const response = await api.post<ApiResponse<City>>('/api/cities', data);
  return response.data.data!;
};

export const getCityLevelStats = async (): Promise<Array<{ level: number; count: number }>> => {
  const response = await api.get<ApiResponse<Array<{ level: number; count: number }>>>('/api/cities/stats/levels');
  return response.data.data!;
};

export const deleteAllCities = async (): Promise<{ deleted: number }> => {
  const response = await api.delete<ApiResponse<{ deleted: number }>>('/api/cities/all');
  return response.data.data!;
};

export const exportCities = async (): Promise<City[]> => {
  const response = await api.get<ApiResponse<City[]>>('/api/cities/export');
  return response.data.data!;
};

// ===== 培训业务管理 API =====
export const getTrainingBusinesses = async (params?: PaginationParams): Promise<PaginatedResponse<TrainingBusiness>> => {
  const response = await api.get<ApiResponse<PaginatedResponse<TrainingBusiness>>>('/api/training-businesses', { params });
  return response.data.data!;
};

export const createTrainingBusiness = async (data: TrainingBusinessFormData): Promise<TrainingBusiness> => {
  const response = await api.post<ApiResponse<TrainingBusiness>>('/api/training-businesses', data);
  return response.data.data!;
};

export const updateTrainingBusiness = async (id: number, data: TrainingBusinessFormData): Promise<TrainingBusiness> => {
  const response = await api.put<ApiResponse<TrainingBusiness>>(`/api/training-businesses/${id}`, data);
  return response.data.data!;
};

export const deleteTrainingBusiness = async (id: number): Promise<void> => {
  await api.delete(`/api/training-businesses/${id}`);
};

export const getTrainingBusinessStats = async (): Promise<{ total: number }> => {
  const response = await api.get<ApiResponse<{ total: number }>>('/api/training-businesses/stats');
  return response.data.data!;
};

export const deleteAllTrainingBusinesses = async (): Promise<{ deleted: number }> => {
  const response = await api.delete<ApiResponse<{ deleted: number }>>('/api/training-businesses/all');
  return response.data.data!;
};

export const exportTrainingBusinesses = async (): Promise<TrainingBusiness[]> => {
  const response = await api.get<ApiResponse<TrainingBusiness[]>>('/api/training-businesses/export');
  return response.data.data!;
};

// ===== 培训要素管理 API =====
export const getTrainingElements = async (params?: PaginationParams): Promise<PaginatedResponse<TrainingElement>> => {
  const response = await api.get<ApiResponse<PaginatedResponse<TrainingElement>>>('/api/training-elements', { params });
  return response.data.data!;
};

export const createTrainingElement = async (data: TrainingElementFormData): Promise<TrainingElement> => {
  const response = await api.post<ApiResponse<TrainingElement>>('/api/training-elements', data);
  return response.data.data!;
};

export const updateTrainingElement = async (id: number, data: TrainingElementFormData): Promise<TrainingElement> => {
  const response = await api.put<ApiResponse<TrainingElement>>(`/api/training-elements/${id}`, data);
  return response.data.data!;
};

export const deleteTrainingElement = async (id: number): Promise<void> => {
  await api.delete(`/api/training-elements/${id}`);
};

export const getTrainingElementStats = async (): Promise<{ total: number }> => {
  const response = await api.get<ApiResponse<{ total: number }>>('/api/training-elements/stats');
  return response.data.data!;
};

export const deleteAllTrainingElements = async (): Promise<{ deleted: number }> => {
  const response = await api.delete<ApiResponse<{ deleted: number }>>('/api/training-elements/all');
  return response.data.data!;
};

export const exportTrainingElements = async (): Promise<TrainingElement[]> => {
  const response = await api.get<ApiResponse<TrainingElement[]>>('/api/training-elements/export');
  return response.data.data!;
};

// ===== 培训标准管理 API =====
export const getTrainingStandards = async (params?: PaginationParams): Promise<PaginatedResponse<TrainingStandard>> => {
  const response = await api.get<ApiResponse<PaginatedResponse<TrainingStandard>>>('/api/training-standards', { params });
  return response.data.data!;
};

export const createTrainingStandard = async (data: TrainingStandardFormData): Promise<TrainingStandard> => {
  const response = await api.post<ApiResponse<TrainingStandard>>('/api/training-standards', data);
  return response.data.data!;
};

export const updateTrainingStandard = async (id: number, data: TrainingStandardFormData): Promise<TrainingStandard> => {
  const response = await api.put<ApiResponse<TrainingStandard>>(`/api/training-standards/${id}`, data);
  return response.data.data!;
};

export const deleteTrainingStandard = async (id: number): Promise<void> => {
  await api.delete(`/api/training-standards/${id}`);
};

export const getTrainingStandardStats = async (): Promise<{ total: number; active: number }> => {
  const response = await api.get<ApiResponse<{ total: number; active: number }>>('/api/training-standards/stats');
  return response.data.data!;
};

export const deleteAllTrainingStandards = async (): Promise<{ deleted: number }> => {
  const response = await api.delete<ApiResponse<{ deleted: number }>>('/api/training-standards/all');
  return response.data.data!;
};

export const exportTrainingStandards = async (): Promise<TrainingStandard[]> => {
  const response = await api.get<ApiResponse<TrainingStandard[]>>('/api/training-standards/export');
  return response.data.data!;
};

// ===== Excel导入 API =====
export const uploadExcelFile = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<ApiResponse>('/api/import/excel', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60000, // 上传超时时间设为60秒
  });
  
  return response.data.data;
};

// 新增：导入已处理的数据（跳过后端评定）
export const uploadProcessedData = async (data: any[], batchId?: string): Promise<any> => {
  const response = await api.post<ApiResponse>('/api/import/processed-data', {
    data,
    batchId
  }, {
    timeout: 60000,
  });
  
  return response.data.data;
};

export const downloadTemplate = async (): Promise<Blob> => {
  const response = await axios.get(`${API_BASE_URL}/api/import/template`, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json',
    }
  });
  
  return response.data;
};

// 下载培训标准导入模板
export const downloadTrainingStandardTemplate = async (): Promise<Blob> => {
  const response = await axios.get(`${API_BASE_URL}/api/training-standards/template`, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json',
    }
  });
  
  return response.data;
};

// 培训标准批量导入
export const importTrainingStandards = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<ApiResponse>('/api/training-standards/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60000, // 上传超时时间设为60秒
  });
  
  return response.data.data;
};

// ===== 检查数据 API =====
export const getInspectionBatches = async (): Promise<BatchInfo[]> => {
  const response = await api.get<ApiResponse<BatchInfo[]>>('/api/inspection/batches');
  return response.data.data!;
};

export const getInspectionBatch = async (batchId: string): Promise<any> => {
  const response = await api.get<ApiResponse>(`/api/inspection/batch/${batchId}`);
  return response.data.data;
};

// ===== 检查报告 API =====
export const getBatchReport = async (batchId: string): Promise<{
  reports: InspectionReport[];
  summary: InspectionSummary;
  batch_id: string;
}> => {
  const response = await api.get<ApiResponse<{
    reports: InspectionReport[];
    summary: InspectionSummary;
    batch_id: string;
  }>>(`/api/reports/batch/${batchId}`);
  return response.data.data!;
};

export const updateReportEvaluation = async (reportId: number, evaluation: string): Promise<void> => {
  await api.put(`/api/reports/${reportId}/evaluation`, { evaluation });
};

export const getWarningStats = async (batchId?: string): Promise<WarningStats> => {
  const url = batchId ? `/api/reports/warnings/${batchId}` : '/api/reports/warnings';
  const fullUrl = `${API_BASE_URL}${url}?t=${Date.now()}`;
  console.log('🔍 [DEBUG] getWarningStats 调用URL:', url);
  console.log('🔍 [DEBUG] API_BASE_URL:', API_BASE_URL);
  console.log('🔍 [DEBUG] 完整URL:', fullUrl);
  const response = await api.get<ApiResponse<WarningStats>>(url + '?t=' + Date.now()); // 添加时间戳防止缓存
  console.log('📊 [DEBUG] API原始响应:', response.data);
  console.log('🔍 [DEBUG] response.data的类型:', typeof response.data.data);
  console.log('🔍 [DEBUG] response.data.data的keys:', Object.keys(response.data.data || {}));
  console.log('📋 [DEBUG] 返回的数据:', response.data.data);
  return response.data.data!;
};

export const exportWarningReport = async (batchId?: string): Promise<Blob> => {
  const url = batchId ? `/api/reports/warnings/${batchId}/export` : '/api/reports/warnings/export';
  const response = await api.get(url, {
    responseType: 'blob',
  });
  return response.data;
};

export const generateBatchReport = async (batchId: string): Promise<{
  success: boolean;
  reports_created: number;
  summary_created: boolean;
  error?: string;
}> => {
  const response = await api.post<ApiResponse<{
    success: boolean;
    reports_created: number;
    summary_created: boolean;
    error?: string;
  }>>(`/api/reports/generate/${batchId}`);
  return response.data.data!;
};

// ===== 统计信息 API =====
export const getStatsOverview = async (): Promise<StatsOverview> => {
  const response = await api.get<ApiResponse<StatsOverview>>('/api/stats/overview');
  return response.data.data!;
};

// ===== 用户认证 API =====

// 用户注册
export const registerUser = async (data: RegisterRequest): Promise<User> => {
  const response = await api.post<ApiResponse<User>>('/api/auth/register', data);
  return response.data.data!;
};

// 用户登录
export const loginUser = async (data: LoginRequest): Promise<LoginResponse> => {
  const response = await api.post<ApiResponse<LoginResponse>>('/api/auth/login', data);
  
  // 登录成功后保存token到localStorage
  if (response.data.data?.token) {
    localStorage.setItem('auth_token', response.data.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.data.user));
  }
  
  return response.data.data!;
};

// 获取当前用户信息
export const getCurrentUser = async (): Promise<User> => {
  const response = await api.get<ApiResponse<User>>('/api/auth/me');
  return response.data.data!;
};

// 登出
export const logout = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
};

// 检查是否已登录
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('auth_token');
  return !!token;
};

// 获取本地存储的用户信息
export const getStoredUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

// ===== 批次删除 API =====

// 批次删除结果接口
export interface BatchDeleteResult {
  batchId: string;
  success: boolean;
  deletedRecords?: {
    data: number;
    reports: number;
    summary: number;
    total: number;
  };
  error?: string;
}

export interface BatchDeleteResponse {
  summary: {
    total: number;
    success: number;
    failure: number;
    totalDeletedRecords: number;
  };
  results: BatchDeleteResult[];
  reason?: string;
}

// 删除单个批次
export const deleteBatch = async (batchId: string): Promise<BatchDeleteResult> => {
  const response = await api.delete<ApiResponse<BatchDeleteResult>>(`/api/inspection/batch/${batchId}`);
  return response.data.data!;
};

// 批量删除批次
export const deleteBatches = async (batchIds: string[], reason?: string): Promise<BatchDeleteResponse> => {
  const response = await api.delete<ApiResponse<BatchDeleteResponse>>('/api/inspection/batches', {
    data: { batchIds, reason }
  });
  return response.data.data!;
};

// ===== 用户管理 API (仅管理员) =====

// 获取所有用户列表
export const getAllUsers = async (): Promise<User[]> => {
  const response = await api.get<ApiResponse<User[]>>('/api/admin/users');
  return response.data.data!;
};

// 更新用户角色
export const updateUserRole = async (userId: number, role: UserRole): Promise<void> => {
  await api.put(`/api/admin/users/${userId}/role`, { role });
};

// 获取用户统计信息
export const getUserStats = async (): Promise<{
  total: number;
  admin: number;
  user: number;
}> => {
  const response = await api.get<ApiResponse<{
    total: number;
    admin: number;
    user: number;
  }>>('/api/admin/users/stats');
  return response.data.data!;
};

// ===== 培训信息收集管理 API =====

// 创建培训信息收集记录
export const createTrainingInfo = async (data: CreateTrainingInfoInput): Promise<TrainingInfoCollection> => {
  const response = await api.post<ApiResponse<TrainingInfoCollection>>('/api/training-info', data);
  return response.data.data!;
};

// 获取培训信息收集列表（带权限控制）
export const getTrainingInfoList = async (params?: TrainingInfoQueryParams): Promise<PaginatedResponse<TrainingInfoCollection>> => {
  const response = await api.get<ApiResponse<PaginatedResponse<TrainingInfoCollection>>>('/api/training-info', {
    params
  });
  return response.data.data!;
};

// 获取单个培训信息收集记录
export const getTrainingInfoById = async (id: number): Promise<TrainingInfoCollection> => {
  const response = await api.get<ApiResponse<TrainingInfoCollection>>(`/api/training-info/${id}`);
  return response.data.data!;
};

// 更新培训信息收集记录
export const updateTrainingInfo = async (id: number, data: Partial<CreateTrainingInfoInput>): Promise<TrainingInfoCollection> => {
  const response = await api.put<ApiResponse<TrainingInfoCollection>>(`/api/training-info/${id}`, data);
  return response.data.data!;
};

// 删除培训信息收集记录
export const deleteTrainingInfo = async (id: number): Promise<void> => {
  await api.delete(`/api/training-info/${id}`);
};

// 获取培训信息统计数据（管理员）
export const getTrainingInfoStats = async (): Promise<TrainingInfoStats> => {
  const response = await api.get<ApiResponse<TrainingInfoStats>>('/api/training-info/stats');
  return response.data.data!;
};

// 导出培训信息数据（仅管理员）
export const exportTrainingInfo = async (request: ExportTrainingInfoRequest): Promise<ExportTrainingInfoResponse> => {
  const response = await api.post<ApiResponse<ExportTrainingInfoResponse>>('/api/training-info/export', request);
  return response.data.data!;
};

// 批量操作培训信息（管理员）
export const batchOperateTrainingInfo = async (request: BatchTrainingInfoRequest): Promise<BatchTrainingInfoResponse> => {
  const response = await api.post<ApiResponse<BatchTrainingInfoResponse>>('/api/training-info/batch', request);
  return response.data.data!;
};

// ===== 培训信息附件管理 API =====

// 上传附件
export const uploadTrainingInfoAttachments = async (trainingInfoId: number, files: File[]): Promise<any[]> => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  const response = await api.post<ApiResponse<any[]>>(
    `/api/training-info/${trainingInfoId}/attachments/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data.data!;
};

// 获取附件列表
export const getTrainingInfoAttachments = async (trainingInfoId: number): Promise<any[]> => {
  const response = await api.get<ApiResponse<any[]>>(`/api/training-info/${trainingInfoId}/attachments`);
  return response.data.data!;
};

// 下载附件
export const downloadTrainingInfoAttachment = async (attachmentId: number, originalName: string): Promise<void> => {
  const response = await api.get(`/api/training-info/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  });
  
  // 创建下载链接
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', originalName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// 删除附件
export const deleteTrainingInfoAttachment = async (attachmentId: number): Promise<void> => {
  await api.delete(`/api/training-info/attachments/${attachmentId}`);
};

// 获取基础数据 - 地区列表（所有用户可访问）
export const getRegionsList = async (): Promise<City[]> => {
  const response = await api.get<ApiResponse<PaginatedResponse<City>>>('/api/basic-data/regions');
  return response.data.data!.data;
};

// 获取基础数据 - 培训业务类型列表（所有用户可访问）
export const getBusinessTypesList = async (): Promise<TrainingBusiness[]> => {
  const response = await api.get<ApiResponse<PaginatedResponse<TrainingBusiness>>>('/api/basic-data/business-types');
  return response.data.data!.data;
};